import type { Profession } from '@continuum/show-core';
import type { Lang } from './text.js';
import type { CrewTitle, JobMood } from './wrappedStats.js';

/**
 * The Wrapped's newer words: the funny card, the saboteur's numbers, the crew title, and the video.
 * The rest of the Wrapped's words are in scoreText.ts.
 *
 * Everything on a card goes out into the world in a story, so the same rules as every outward word:
 * no brands, no dates, no times of day, no years (eras by name), and the plain words the show uses —
 * saboteur, alarm, FIX, damage, voted out. Light-hearted, never mean: a guest should be glad to post it.
 */

export interface WrappedCopy {
  /** The ship card's third row, where the votes cast used to be. */
  rightVotes: (n: number) => string;
  /** The funny card. */
  otherKicker: string;
  wronglyOut: (n: number) => string;
  clumsy: (game: string) => string;
  /** `over`: slower than its target. */
  slow: (game: string, over: boolean) => string;
  near: (n: number) => string;
  clean: (n: number) => string;
  /** A saboteur's own numbers, on the role card. */
  gotAway: string;
  caught: string;
  damageDone: string;
  /** The crew title, the last card. */
  titleKicker: string;
  title: (kind: CrewTitle, post: Profession | null) => string;
  titleWhy: (kind: CrewTitle) => string;
  /** The name slide: what the call sign says about the guest, and how the evening bore it out. */
  nameKicker: string;
  nameLine: (callsign: string) => string;
  nameTie: (callsign: string, era: string | null) => string;
  /** The job slide. */
  jobKicker: string;
  jobLine: (post: Profession, mood: JobMood) => string;
  jobGames: string;
  jobBest: string;
  jobMistakes: string;
  pace: (pace: number) => string;
  /** The crew slide: the guest's own messages, in counts, and what they did with the others. */
  crewKicker: string;
  chatSent: (n: number, people: number) => string;
  chatGot: (n: number) => string;
  /** The story player. */
  storyHint: string;
  /** The video. */
  videoMake: string;
  videoMakeSub: (seconds: number) => string;
  videoSaveHint: string;
  videoDownloadHint: string;
  videoSee: string;
  videoMaking: (secondsLeft: number) => string;
  videoStay: string;
  videoReady: string;
  videoSave: string;
  videoStop: string;
  videoBack: string;
  videoAgain: string;
  videoHidden: string;
  videoFailed: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const EN_POST: Record<Profession, string> = {
  NAVIGATOR: 'NAVIGATOR',
  ENGINEER: 'ENGINEER',
  BIOTECHNICIAN: 'BIOTECHNICIAN',
  'SIGNAL OFFICER': 'SIGNAL OFFICER',
  ARCHIVIST: 'ARCHIVIST',
  QUARTERMASTER: 'QUARTERMASTER',
};

/** The posts in bokmål, definite: "the navigator". */
const NO_POST_THE: Record<Profession, string> = {
  NAVIGATOR: 'NAVIGATØREN',
  ENGINEER: 'INGENIØREN',
  BIOTECHNICIAN: 'BIOTEKNIKEREN',
  'SIGNAL OFFICER': 'SIGNALOFFISEREN',
  ARCHIVIST: 'ARKIVAREN',
  QUARTERMASTER: 'KVARTERMESTEREN',
};

const EN_TITLE: Record<CrewTitle, (post: string) => string> = {
  masterSaboteur: () => 'THE MASTER SABOTEUR',
  slipperySaboteur: () => 'THE SLIPPERY SABOTEUR',
  boldSaboteur: () => 'THE BOLD SABOTEUR',
  ace: () => 'THE ACE',
  speedDemon: () => 'THE SPEED DEMON',
  sharpEye: () => 'THE SHARP EYE',
  fixer: () => 'THE FIXER',
  chatterbox: () => 'THE CHATTERBOX',
  misunderstood: () => 'THE MISUNDERSTOOD',
  calm: (post) => `THE CALM ${post}`,
  stubborn: (post) => `THE STUBBORN ${post}`,
  usualSuspect: () => 'THE USUAL SUSPECT',
  loyal: (post) => `THE LOYAL ${post}`,
};

const EN_WHY: Record<CrewTitle, string> = {
  masterSaboteur: 'A saboteur, and never caught.',
  slipperySaboteur: 'Caught, but not every time.',
  boldSaboteur: 'Caught in the act, and loved every minute.',
  ace: 'Top of the table at the end.',
  speedDemon: 'Beat the target by a mile.',
  sharpEye: 'Spotted the saboteurs, again and again.',
  fixer: 'Alarms went off. You fixed them.',
  chatterbox: 'Never short of a word, all 765 years.',
  misunderstood: 'Voted out, and innocent all along.',
  calm: 'Not one mistake all evening.',
  stubborn: 'Never gave up. Not once.',
  usualSuspect: 'Somehow always near the alarm.',
  loyal: 'Kept the ship going, all 765 years.',
};

const NO_TITLE: Record<CrewTitle, (post: string) => string> = {
  masterSaboteur: () => 'MESTERSABOTØREN',
  slipperySaboteur: () => 'DEN GLATTE SABOTØREN',
  boldSaboteur: () => 'DEN DRISTIGE SABOTØREN',
  ace: () => 'ESSET',
  speedDemon: () => 'FARTSDEMONEN',
  sharpEye: () => 'ØRNEØYET',
  fixer: () => 'FIKSEREN',
  chatterbox: () => 'PRATMAKEREN',
  misunderstood: () => 'DEN MISFORSTÅTTE',
  calm: (post) => `DEN ROLIGE ${post}`,
  stubborn: (post) => `DEN STAE ${post}`,
  usualSuspect: () => 'DEN VANLIGE MISTENKTE',
  loyal: (post) => `DEN LOJALE ${post}`,
};

const NO_WHY: Record<CrewTitle, string> = {
  masterSaboteur: 'Sabotør, og aldri tatt.',
  slipperySaboteur: 'Tatt, men ikke hver gang.',
  boldSaboteur: 'Tatt på fersken, og nøt hvert minutt.',
  ace: 'Øverst ved bordet til slutt.',
  speedDemon: 'Slo måltiden med god margin.',
  sharpEye: 'Avslørte sabotørene, gang på gang.',
  fixer: 'Alarmene gikk. Du fikset dem.',
  chatterbox: 'Aldri stille, alle 765 år.',
  misunderstood: 'Stemt ut, og uskyldig hele tiden.',
  calm: 'Ikke én feil hele kvelden.',
  stubborn: 'Ga aldri opp. Ikke én gang.',
  usualSuspect: 'Alltid i nærheten når alarmen gikk.',
  loyal: 'Holdt skipet i gang, alle 765 år.',
};

/**
 * What each call sign says about the guest who carried it: made up, playful, never mean. One for every name
 * in the roster (show-core `CALLSIGNS`); a name from outside it gets the line every name could have.
 */
const EN_NAME: Record<string, string> = {
  VEGA: 'One of the brightest stars in the night sky. You do not blend in, and you did not tonight.',
  KESTREL: 'A small falcon that hangs still in the wind, then dives. Patient, then very, very fast.',
  JUNO: 'Named after a queen of the old sky. Calm, in charge, and quietly keeping score.',
  SABLE: 'The deepest black there is. Mysterious. Possibly up to something.',
  LYRA: 'A little constellation shaped like a harp. Small, bright and in tune with the whole table.',
  'ORION-B': 'The hunter’s second. Every great hunter needs one, and the second is often the better.',
  HALCYON: 'An old word for calm, golden days. Nothing rattles you, not even an alarm.',
  MIRA: 'A star that glows brighter, then softer, then brighter again. Full of surprises.',
  TALA: 'A name as short as a wink. Easy to remember, hard to forget.',
  CYGNUS: 'The swan of the summer sky. Graceful on the surface, paddling like mad underneath.',
  ROOK: 'The piece that moves in straight lines and guards the corners. Direct, loyal, hard to get past.',
  EMBER: 'The glow that stays when the fire is low. Quiet, warm, and still going at the end.',
};

const NO_NAME: Record<string, string> = {
  VEGA: 'En av de klareste stjernene på nattehimmelen. Du glir ikke inn i mengden, og det gjorde du ikke i kveld.',
  KESTREL: 'En liten falk som står stille i vinden, og så stuper. Tålmodig, så lynrask.',
  JUNO: 'Oppkalt etter en dronning på den gamle himmelen. Rolig, har kontrollen, og holder stille oversikt over poengene.',
  SABLE: 'Den dypeste svartfargen som finnes. Mystisk. Kanskje i gang med noe.',
  LYRA: 'Et lite stjernebilde formet som en harpe. Liten, klar og i takt med hele bordet.',
  'ORION-B': 'Jegerens nummer to. Enhver stor jeger trenger en, og nummer to er ofte den beste.',
  HALCYON: 'Et gammelt ord for rolige, gylne dager. Ingenting bringer deg ut av fatning, ikke engang en alarm.',
  MIRA: 'En stjerne som lyser sterkere, så svakere, så sterkere igjen. Full av overraskelser.',
  TALA: 'Et navn så kort som et blunk. Lett å huske, vanskelig å glemme.',
  CYGNUS: 'Svanen på sommerhimmelen. Elegant på overflaten, padler som en gal under vann.',
  ROOK: 'Tårnet på sjakkbrettet: går i rette linjer og vokter hjørnene. Rett på sak, lojal, vanskelig å komme forbi.',
  EMBER: 'Gløden som blir igjen når bålet brenner lavt. Stille, varm, og fortsatt i gang til slutt.',
};

/** A line for each post, by how its games went (wrappedStats.ts `jobMood`). */
const EN_JOB: Record<Profession, Record<JobMood, string>> = {
  NAVIGATOR: {
    fast: 'Shortest route, every time. The stars barely saw you pass.',
    clean: 'Not one wrong turn in 765 years.',
    scenic: 'You took the scenic route. The view was worth it.',
    none: 'Tonight the course could wait. Other things needed steering.',
  },
  ENGINEER: {
    fast: 'Engines running before anyone found the manual.',
    clean: 'Not a single spark out of place.',
    scenic: 'A few bangs, a little smoke, and it all still runs.',
    none: 'Tonight the engine ran itself. You had other things to fix.',
  },
  BIOTECHNICIAN: {
    fast: 'Plants grew faster just because you looked at them.',
    clean: 'Every seedling made it. Every single one.',
    scenic: 'A few leaves fell along the way. The garden forgives you.',
    none: 'Tonight the garden grew on its own. You were busy elsewhere.',
  },
  'SIGNAL OFFICER': {
    fast: 'Message received before it was even sent.',
    clean: 'Every message loud and clear.',
    scenic: 'A little static on the line, but every word got through.',
    none: 'Tonight the radio was quiet. You were busy elsewhere.',
  },
  ARCHIVIST: {
    fast: 'Found it before anyone had finished asking.',
    clean: 'Every record in its proper place.',
    scenic: 'Filed a few things in creative places.',
    none: 'Tonight the archive kept itself. You were busy elsewhere.',
  },
  QUARTERMASTER: {
    fast: 'Shared out before the queue could even form.',
    clean: 'Every share fair, down to the last crumb.',
    scenic: 'Counted twice, shared once, and nobody went hungry.',
    none: 'Tonight the stores looked after themselves. You were busy elsewhere.',
  },
};

const NO_JOB: Record<Profession, Record<JobMood, string>> = {
  NAVIGATOR: {
    fast: 'Korteste vei, hver gang. Stjernene rakk knapt å se deg.',
    clean: 'Ikke én feil sving på 765 år.',
    scenic: 'Du tok den lange veien. Utsikten var verdt det.',
    none: 'I kveld fikk kursen vente. Andre ting måtte styres.',
  },
  ENGINEER: {
    fast: 'Motoren gikk før noen fant bruksanvisningen.',
    clean: 'Ikke én gnist på feil sted.',
    scenic: 'Litt smell, litt røyk, og alt går fortsatt.',
    none: 'I kveld gikk motoren av seg selv. Du hadde andre ting å fikse.',
  },
  BIOTECHNICIAN: {
    fast: 'Plantene vokste fortere bare fordi du så på dem.',
    clean: 'Hver eneste spire klarte seg.',
    scenic: 'Noen blader falt på veien. Hagen tilgir deg.',
    none: 'I kveld vokste hagen av seg selv. Du hadde nok å gjøre.',
  },
  'SIGNAL OFFICER': {
    fast: 'Meldingen var mottatt før den var sendt.',
    clean: 'Hver melding høyt og tydelig.',
    scenic: 'Litt skurring på linjen, men hvert ord kom fram.',
    none: 'I kveld var radioen stille. Du hadde nok å gjøre.',
  },
  ARCHIVIST: {
    fast: 'Fant det før noen var ferdige med å spørre.',
    clean: 'Hver post på sin rette plass.',
    scenic: 'Arkiverte et par ting på kreative steder.',
    none: 'I kveld passet arkivet seg selv. Du hadde nok å gjøre.',
  },
  QUARTERMASTER: {
    fast: 'Delt ut før køen rakk å danne seg.',
    clean: 'Hver del rettferdig, ned til siste smule.',
    scenic: 'Talt to ganger, delt én gang, og ingen gikk sultne.',
    none: 'I kveld passet lageret seg selv. Du hadde nok å gjøre.',
  },
};

/** Every call sign that has its own line, for the tests. */
export const NAMED_CALLSIGNS: readonly string[] = Object.keys(EN_NAME);

const en: WrappedCopy = {
  rightVotes: (n) => `${plural(n, 'VOTE', 'VOTES')} THAT HIT A SABOTEUR`,
  otherKicker: 'THE OTHER NUMBERS',
  wronglyOut: (n) => `${plural(n, 'TIME', 'TIMES')} VOTED OUT, AND INNOCENT. THE TABLE OWES YOU ONE.`,
  clumsy: (game) => `MISTAKES IN ${game}. BUTTER FINGERS.`,
  slow: (game, over) => (over ? `ON ${game}. YOU TOOK THE SCENIC ROUTE.` : `YOUR SLOWEST, ON ${game}. STILL UNDER TARGET.`),
  near: (n) => (n === 1 ? 'ALARM WENT OFF RIGHT NEAR YOUR SEAT. JUST A COINCIDENCE?' : 'ALARMS WENT OFF RIGHT NEAR YOUR SEAT. FUNNY, THAT.'),
  clean: (n) => `${plural(n, 'GAME', 'GAMES')} WITHOUT A SINGLE MISTAKE`,
  gotAway: 'GOT AWAY',
  caught: 'CAUGHT',
  damageDone: 'DAMAGE DONE',
  titleKicker: 'YOUR CREW TITLE',
  title: (kind, post) => EN_TITLE[kind](post ? EN_POST[post] : 'SPACEFARER'),
  titleWhy: (kind) => EN_WHY[kind],
  nameKicker: 'WHAT YOUR NAME SAYS',
  nameLine: (callsign) => EN_NAME[callsign] ?? 'A name the ship will remember long after the lights go down.',
  nameTie: (callsign, era) => (era ? `${callsign} SHONE BRIGHTEST IN ${era}.` : `${callsign} WAS ABOARD FOR ALL 765 YEARS.`),
  jobKicker: 'YOUR JOB TONIGHT',
  jobLine: (post, mood) => EN_JOB[post][mood],
  jobGames: 'GAMES',
  jobBest: 'BEST PACE',
  jobMistakes: 'MISTAKES',
  pace: (pace) => `${pace.toFixed(1)}×`,
  crewKicker: 'YOU AND THE CREW',
  chatSent: (n, people) =>
    `${plural(n, 'MESSAGE', 'MESSAGES')} YOU SENT${people > 0 ? `, TO ${people} ${plural(people, 'CREWMATE', 'CREWMATES')}` : ''}`,
  chatGot: (n) => `${plural(n, 'MESSAGE', 'MESSAGES')} THAT CAME YOUR WAY`,
  storyHint: 'tap the edges to move · hold to pause',
  videoMake: 'SAVE THE VIDEO',
  videoMakeSub: (seconds) => `made here in ${seconds} seconds · no sound`,
  videoSaveHint: 'Choose Save Video to keep it in your photos.',
  videoDownloadHint: 'It goes to your downloads.',
  videoSee: 'SEE YOUR VIDEO',
  videoMaking: (left) => `MAKING YOUR VIDEO… ${left} S`,
  videoStay: 'Keep this screen open while it records.',
  videoReady: 'No sound on purpose: add your own music when you post it.',
  videoSave: 'SAVE THE VIDEO',
  videoStop: 'STOP',
  videoBack: 'BACK TO THE CARDS',
  videoAgain: 'TRY AGAIN',
  videoHidden: 'The video stopped when the screen went away. Keep this screen open and try again.',
  videoFailed: 'This phone could not make the video. The cards still save one by one.',
};

const no: WrappedCopy = {
  rightVotes: (n) => `${plural(n, 'STEMME', 'STEMMER')} SOM TRAFF EN SABOTØR`,
  otherKicker: 'DE ANDRE TALLENE',
  wronglyOut: (n) => `${plural(n, 'GANG', 'GANGER')} STEMT UT, OG USKYLDIG. BORDET SKYLDER DEG EN.`,
  clumsy: (game) => `FEIL I ${game}. SMØRFINGRE.`,
  slow: (game, over) => (over ? `PÅ ${game}. DU TOK DEN LANGE VEIEN.` : `DITT TREGESTE, PÅ ${game}. LIKEVEL UNDER MÅLET.`),
  near: (n) => (n === 1 ? 'ALARM GIKK RETT VED PLASSEN DIN. BARE TILFELDIG?' : 'ALARMER GIKK RETT VED PLASSEN DIN. MERKELIG, DET.'),
  clean: (n) => `${plural(n, 'SPILL', 'SPILL')} UTEN EN ENESTE FEIL`,
  gotAway: 'SLAPP UNNA',
  caught: 'TATT',
  damageDone: 'SKADE GJORT',
  titleKicker: 'DIN TITTEL OM BORD',
  title: (kind, post) => NO_TITLE[kind](post ? NO_POST_THE[post] : 'ROMFAREREN'),
  titleWhy: (kind) => NO_WHY[kind],
  nameKicker: 'DETTE SIER NAVNET DITT',
  nameLine: (callsign) => NO_NAME[callsign] ?? 'Et navn skipet husker lenge etter at lyset er slukket.',
  nameTie: (callsign, era) => (era ? `${callsign} LYSTE STERKEST I ${era}.` : `${callsign} VAR OM BORD I ALLE 765 ÅR.`),
  jobKicker: 'JOBBEN DIN I KVELD',
  jobLine: (post, mood) => NO_JOB[post][mood],
  jobGames: 'SPILL',
  jobBest: 'BESTE TEMPO',
  jobMistakes: 'FEIL',
  pace: (pace) => `${pace.toFixed(1).replace('.', ',')}×`,
  crewKicker: 'DU OG MANNSKAPET',
  chatSent: (n, people) =>
    `${plural(n, 'MELDING', 'MELDINGER')} DU SENDTE${people > 0 ? `, TIL ${people} I MANNSKAPET` : ''}`,
  chatGot: (n) => `${plural(n, 'MELDING', 'MELDINGER')} SOM KOM TIL DEG`,
  storyHint: 'trykk på kantene for å bla · hold for pause',
  videoMake: 'LAGRE VIDEOEN',
  videoMakeSub: (seconds) => `lages her på ${seconds} sekunder · uten lyd`,
  videoSaveHint: 'Velg «Arkiver video» for å ha den i Bilder.',
  videoDownloadHint: 'Den havner i nedlastinger.',
  videoSee: 'SE VIDEOEN DIN',
  videoMaking: (left) => `LAGER VIDEOEN DIN… ${left} S`,
  videoStay: 'Hold skjermen åpen mens den tar opp.',
  videoReady: 'Uten lyd med vilje: legg på din egen musikk når du deler.',
  videoSave: 'LAGRE VIDEOEN',
  videoStop: 'STOPP',
  videoBack: 'TILBAKE TIL KORTENE',
  videoAgain: 'PRØV IGJEN',
  videoHidden: 'Videoen stoppet da skjermen ble borte. Hold skjermen åpen og prøv igjen.',
  videoFailed: 'Telefonen klarte ikke å lage videoen. Kortene kan fortsatt lagres ett og ett.',
};

export const WRAPPED_COPY: Record<Lang, WrappedCopy> = { en, no };
